import type { ListEventsParams } from "@project-x/api-clients";
import { nowInZone, toUtcIso } from "@/utils/timezone";

export type EventsDatePreset = "today" | "week" | "month" | "popular";

export interface EventsFilterState {
  state?: string | null;
  /** Word search for event name/description (case-insensitive on backend). */
  query?: string | null;
  datePreset?: EventsDatePreset | null;
}

export const DEFAULT_EVENTS_FILTERS: EventsFilterState = {
  state: null,
  query: null,
  datePreset: null,
};

/**
 * Returns true when any filter differs from defaults (used for header “clear” affordance).
 */
export const hasActiveFilters = (filters: EventsFilterState): boolean =>
  JSON.stringify(filters) !== JSON.stringify(DEFAULT_EVENTS_FILTERS);

/**
 * Resolves date preset ranges for event filtering using the default timezone.
 */
export const resolveDatePresetRange = (
  preset: EventsDatePreset | null | undefined,
): { startsFrom?: string; startsTo?: string } => {
  if (!preset) {
    return {};
  }

  const now = nowInZone();
  const startOfToday = now.startOf("day");
  const endsOf = {
    today: startOfToday.endOf("day"),
    week: startOfToday.plus({ days: 7 }).endOf("day"),
    month: startOfToday.plus({ months: 1 }).endOf("day"),
  };
  const ends =
    preset === "week" || preset === "popular"
      ? endsOf.week
      : preset === "month"
        ? endsOf.month
        : endsOf.today;

  return {
    startsFrom: toUtcIso(now),
    startsTo: toUtcIso(ends),
  };
};

/**
 * When the "popular" date preset is active, override orderBy to "ticket_revenue"
 * so the list actually shows the most popular (highest-revenue) events first.
 */
export const resolveListOrderBy = (
  filters: EventsFilterState,
  callerOrderBy: ListEventsParams["orderBy"],
): ListEventsParams["orderBy"] =>
  filters.datePreset === "popular" ? "ticket_revenue" : callerOrderBy;

export const buildListEventsParams = (
  filters: EventsFilterState,
  overrides: Pick<ListEventsParams, "page" | "pageSize" | "orderBy">,
): ListEventsParams => {
  const params: ListEventsParams = {
    status: "PUBLISHED",
    orderBy: resolveListOrderBy(filters, overrides.orderBy),
    page: overrides.page,
    pageSize: overrides.pageSize,
  };

  if (filters.state) {
    params.state = filters.state;
  }
  const searchQuery = filters.query?.trim();
  if (searchQuery) {
    params.search = searchQuery;
  }

  const range = resolveDatePresetRange(filters.datePreset ?? null);
  if (range.startsFrom) {
    params.startsFrom = range.startsFrom;
  }
  if (range.startsTo) {
    params.startsTo = range.startsTo;
  }

  return params;
};

export const serializeFiltersToQuery = (filters: EventsFilterState): string => {
  const params = new URLSearchParams();
  if (filters.state) {
    params.set("state", filters.state);
  }
  const q = filters.query?.trim();
  if (q) {
    params.set("q", q);
  }
  if (filters.datePreset) {
    params.set("date", filters.datePreset);
  }
  return params.toString();
};

export const parseFiltersFromQuery = (searchParams: URLSearchParams): EventsFilterState => {
  const filters: EventsFilterState = { ...DEFAULT_EVENTS_FILTERS };

  const state = searchParams.get("state");
  const q = searchParams.get("q");
  const datePreset = searchParams.get("date") as EventsDatePreset | null;

  if (state) {
    filters.state = state;
  }
  if (q?.trim()) {
    filters.query = q.trim();
  }
  if (datePreset && ["today", "week", "month", "popular"].includes(datePreset)) {
    filters.datePreset = datePreset;
  }

  return filters;
};
